/**
 * Movement rule framework.
 *
 * Rules are the extension point for new movement interactions. A rule does
 * not get free access to player state: it declares a stage, an order, its
 * preconditions, its conflicts and an energy policy, and the motor calls it
 * with a read-only context plus the single quantity that stage may change.
 * The motor enforces the declared energy policy after every rule call.
 *
 * Stages (in tick order, see MOVEMENT_SPEC.md "Tick pipeline"):
 *  - field:     add an acceleration (forces/fields). Summed, order-independent.
 *  - transform: transform the velocity vector in place (ordered by `order`).
 *  - jump:      contribute an impulse when a jump is requested; may consume it.
 *  - impact:    adjust the outgoing velocity after a collision response.
 */
import type { Vec3 } from '../core/Vec3';
import type { MovementConfig } from '../core/Config';
import type { ProximityContact } from '../simulation/CollisionQueries';
import type { SurfaceMaterialDef } from '../world/SurfaceMaterials';
import type { PlayerState, RuleState } from './PlayerState';

export type RuleStage = 'field' | 'transform' | 'jump' | 'impact';

/**
 * conserve:   |v| must be unchanged (pure redirection).
 * dissipate:  |v| must not increase.
 * inject:     may add energy (must stay finite).
 */
export type EnergyPolicy = 'conserve' | 'dissipate' | 'inject';

export interface MovementIntent {
  /** Unit wish direction perpendicular to `up`, or zero vector when no input. */
  readonly wishDir: Readonly<Vec3>;
  /** 0..1 magnitude of the analogue/digital input. */
  readonly wishStrength: number;
  readonly jumpRequested: boolean;
  readonly jumpHeld: boolean;
}

export interface RuleContext {
  readonly state: Readonly<PlayerState>;
  readonly intent: MovementIntent;
  readonly movement: Readonly<MovementConfig>;
  readonly dt: number;
  /** Solids near the capsule at the start of the tick (within the max rule range). */
  readonly nearby: readonly ProximityContact[];
  readonly nearbyCount: number;
  /** Persistent state owned by the rule being called (null if it declared none). */
  readonly ruleState: RuleState | null;
}

export interface ImpactInfo {
  /** Surface normal, pointing toward the player. */
  readonly normal: Readonly<Vec3>;
  /** Velocity just before the impact response. */
  readonly incoming: Readonly<Vec3>;
  /** Speed into the surface, -(incoming · normal) > 0. */
  readonly impactSpeed: number;
  readonly material: SurfaceMaterialDef | null;
  readonly colliderId: number;
}

export interface ParamSpec {
  readonly min: number;
  readonly max: number;
  readonly description: string;
}

interface RuleBase<P extends Record<string, number>> {
  readonly id: string;
  readonly description: string;
  readonly order: number;
  readonly conflictsWith: readonly string[];
  /** Rules that must be enabled before this one (e.g. a release rule needs its store rule). */
  readonly requires?: readonly string[];
  /** Share persistent state with another rule (defaults to own id). */
  readonly stateKey?: string;
  readonly energy: EnergyPolicy;
  /** Distance within which this rule needs nearby-surface information (0 = none). */
  proximityRange(params: Readonly<P>): number;
  readonly params: P;
  readonly paramSpecs: { readonly [K in keyof P]: ParamSpec };
  /** Creates the rule's persistent per-player state, if any. */
  createState?(): RuleState;
  /** Precondition. The rule is skipped for this tick when false. */
  applies(ctx: RuleContext, params: Readonly<P>): boolean;
}

export interface FieldRule<P extends Record<string, number> = Record<string, number>> extends RuleBase<P> {
  readonly stage: 'field';
  /** Add this rule's acceleration (m/s²) into `outAccel`. */
  accumulate(ctx: RuleContext, params: Readonly<P>, outAccel: Vec3): void;
}

export interface TransformRule<P extends Record<string, number> = Record<string, number>> extends RuleBase<P> {
  readonly stage: 'transform';
  transform(ctx: RuleContext, params: Readonly<P>, velocity: Vec3): void;
}

export interface JumpRule<P extends Record<string, number> = Record<string, number>> extends RuleBase<P> {
  readonly stage: 'jump';
  /** Add an impulse (m/s) into `outImpulse`. Return true if the jump request was consumed. */
  contribute(ctx: RuleContext, params: Readonly<P>, groundJumpAvailable: boolean, outImpulse: Vec3): boolean;
}

export interface ImpactRule<P extends Record<string, number> = Record<string, number>> extends RuleBase<P> {
  readonly stage: 'impact';
  /** `velocity` holds the default slide response; the rule may modify it. */
  onImpact(ctx: RuleContext, params: Readonly<P>, impact: ImpactInfo, velocity: Vec3): void;
}

export type MovementRule = FieldRule | TransformRule | JumpRule | ImpactRule;

/**
 * Ordered, conflict-checked collection of rules. Disabled rules are never
 * called, so a disabled experiment cannot influence baseline movement.
 */
export class MovementRuleSet {
  private readonly rules = new Map<string, MovementRule>();
  private readonly enabled = new Set<string>();
  private sorted: Record<RuleStage, MovementRule[]> = { field: [], transform: [], jump: [], impact: [] };

  register(rule: MovementRule, enabled = false): void {
    if (this.rules.has(rule.id)) throw new Error(`Duplicate movement rule "${rule.id}"`);
    this.rules.set(rule.id, rule);
    if (enabled) this.enable(rule.id);
    else this.rebuild();
  }

  get(id: string): MovementRule {
    const r = this.rules.get(id);
    if (!r) throw new Error(`Unknown movement rule "${id}"`);
    return r;
  }

  list(): MovementRule[] {
    return [...this.rules.values()];
  }

  isEnabled(id: string): boolean {
    return this.enabled.has(id);
  }

  enable(id: string): void {
    const rule = this.get(id);
    for (const req of rule.requires ?? []) {
      if (!this.enabled.has(req)) throw new Error(`Movement rule "${id}" requires "${req}" to be enabled first`);
    }
    for (const other of this.enabled) {
      const o = this.get(other);
      if (rule.conflictsWith.includes(other) || o.conflictsWith.includes(id)) {
        throw new Error(`Movement rule "${id}" conflicts with enabled rule "${other}"`);
      }
    }
    this.enabled.add(id);
    this.rebuild();
  }

  disable(id: string): void {
    this.get(id);
    for (const other of this.enabled) {
      if (this.get(other).requires?.includes(id)) throw new Error(`Cannot disable "${id}": enabled rule "${other}" requires it`);
    }
    this.enabled.delete(id);
    this.rebuild();
  }

  setEnabled(id: string, on: boolean): void {
    if (on) this.enable(id);
    else this.disable(id);
  }

  setParam(id: string, key: string, value: number): void {
    const rule = this.get(id);
    const spec = (rule.paramSpecs as Record<string, ParamSpec | undefined>)[key];
    if (!spec) throw new Error(`Rule "${id}" has no parameter "${key}"`);
    if (!(Number.isFinite(value) && value >= spec.min && value <= spec.max)) {
      throw new Error(`Rule "${id}" parameter "${key}" must be within [${spec.min}, ${spec.max}] (got ${value})`);
    }
    (rule.params as Record<string, number>)[key] = value;
  }

  stage<S extends RuleStage>(stage: S): readonly Extract<MovementRule, { stage: S }>[] {
    return this.sorted[stage] as Extract<MovementRule, { stage: S }>[];
  }

  /** Largest proximity range any enabled rule needs. */
  maxProximityRange(): number {
    let r = 0;
    for (const id of this.enabled) {
      const rule = this.get(id);
      r = Math.max(r, rule.proximityRange(rule.params));
    }
    return r;
  }

  enabledIds(): string[] {
    return [...this.enabled].sort();
  }

  private rebuild(): void {
    const next: Record<RuleStage, MovementRule[]> = { field: [], transform: [], jump: [], impact: [] };
    for (const id of this.enabled) {
      const r = this.get(id);
      next[r.stage].push(r);
    }
    for (const list of Object.values(next)) list.sort((a, b) => a.order - b.order || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
    this.sorted = next;
  }
}
